import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Store, Factory, Trees, ArrowRight, Shield, MessageSquare, MapPin, Sparkles, Handshake, Search as SearchIcon, HeartHandshake, Zap, PhoneCall, Users, Home as HomeIcon, Award, Star } from "lucide-react";
import HeroSearch from "@/components/site/HeroSearch";
import PropertyCard from "@/components/site/PropertyCard";
import { CATEGORY_IMAGES } from "@/data/constants";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

function useCountUp(target, duration = 1500) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => { if (!start) start = ts; const p = Math.min((ts - start) / duration, 1);
      setN(Math.floor(p * target)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

const StatCounter = ({ v, label, suffix = "+" }) => {
  const n = useCountUp(v);
  return (
    <div className="text-center">
      <div className="font-display text-4xl sm:text-5xl font-bold text-kp-maroon">{n.toLocaleString("en-IN")}{suffix}</div>
      <div className="text-sm text-slate-600 mt-1">{label}</div>
    </div>
  );
};

export default function Home({ onLoginClick }) {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/properties?featured=true&limit=8").then(r => setFeatured(r.data)).catch(() => {});
    api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const cats = [
    { key: "residential", icon: Building2, name: t("categories.res"), items: "Flats · Apartments · Villas · Bungalows", to: "/properties?category=residential" },
    { key: "commercial", icon: Store, name: t("categories.com"), items: "Shops · Offices · Showrooms", to: "/properties?category=commercial" },
    { key: "industrial", icon: Factory, name: t("categories.ind"), items: "Warehouses · Factories · Sheds · GIDC", to: "/properties?category=industrial" },
    { key: "plot", icon: Trees, name: t("categories.plot"), items: "Open Plots · Farm Land · NA Plots", to: "/properties?category=plot" },
  ];

  const purposes = [
    { icon: SearchIcon, title: t("purpose.buy"), desc: t("purpose.buy_desc"), to: "/properties?purpose=sale", color: "kp-teal" },
    { icon: HeartHandshake, title: t("purpose.sale"), desc: t("purpose.sale_desc"), to: "/post-property", color: "kp-maroon" },
    { icon: HomeIcon, title: t("purpose.rent"), desc: t("purpose.rent_desc"), to: "/properties?purpose=rent", color: "kp-teal" },
    { icon: Sparkles, title: t("purpose.post"), desc: t("purpose.post_desc"), to: "/post-property", color: "kp-maroon" },
  ];

  const whyItems = [Shield, Handshake, MapPin, Sparkles, Building2, Zap, HeartHandshake, SearchIcon];

  return (
    <>
      <HeroSearch/>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-kp-teal">Discover</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">{t("categories.title")}</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {cats.map((c) => (
            <Link key={c.key} to={c.to} className="group relative overflow-hidden rounded-2xl border border-slate-200 card-hover" data-testid={`cat-card-${c.key}`}>
              <img src={CATEGORY_IMAGES[c.key]} alt={c.name} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"/>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent"/>
              <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                <div className="w-11 h-11 rounded-full bg-kp-maroon flex items-center justify-center mb-3"><c.icon size={20}/></div>
                <h3 className="font-display text-xl font-semibold">{c.name}</h3>
                <p className="text-xs text-white/80 mt-1">{c.items}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-kp-maroon-light group-hover:gap-2 transition-all">{t("categories.view")} <ArrowRight size={14}/></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Purpose */}
      <section className="bg-kp-grey py-16">
        <div className="max-w-[1400px] mx-auto px-4">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-10">{t("purpose.title")}</h2>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {purposes.map((p, i) => (
              <Link key={i} to={p.to} className="bg-white rounded-2xl p-6 border border-slate-200 card-hover" data-testid={`purpose-${i}`}>
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${p.color === "kp-teal" ? "bg-kp-teal-light text-kp-teal" : "bg-kp-maroon-light text-kp-maroon"}`}><p.icon size={26}/></div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{p.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-kp-teal group-hover:gap-2">Explore <ArrowRight size={14}/></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="max-w-[1400px] mx-auto px-4 py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-kp-teal">Hand-picked</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">{t("featured.title")}</h2>
            <p className="text-slate-600 mt-2 max-w-2xl">{t("featured.sub")}</p>
          </div>
          <Link to="/properties" className="hidden md:inline-flex items-center gap-1 text-kp-maroon font-semibold hover:gap-2 transition-all" data-testid="link-view-all">
            {t("featured.viewAll")} <ArrowRight size={16}/>
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {featured.map((p) => <PropertyCard key={p.id} property={p} onLoginRequired={onLoginClick}/>)}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-kp-teal-dark text-white py-10 sm:py-16">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="font-display text-2xl sm:text-4xl font-bold">{t("why.title")}</h2>
            <p className="text-white/70 mt-2 text-sm sm:text-base">{t("why.sub")}</p>
          </div>
          <div className="grid gap-2.5 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            {whyItems.map((Icon, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-5 hover:bg-white/10 transition-colors">
                <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-kp-maroon flex items-center justify-center mb-2 sm:mb-3"><Icon size={16} className="sm:hidden"/><Icon size={20} className="hidden sm:block"/></div>
                <h3 className="font-display text-sm sm:text-lg font-semibold leading-tight">{t(`why.f${i+1}`)}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-kp-teal-light rounded-3xl p-10">
          <StatCounter v={1250} label={t("stats.p")}/>
          <StatCounter v={500} label={t("stats.c")}/>
          <StatCounter v={50} label={t("stats.a")}/>
          <StatCounter v={100} label={t("stats.pr")}/>
        </div>
      </section>

      {/* Projects */}
      <section className="bg-kp-grey py-16">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{t("projects.title")}</h2>
            <p className="text-slate-600 mt-2">{t("projects.sub")}</p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {projects.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover">
                <img src={p.image} alt={p.name} className="w-full h-44 object-cover"/>
                <div className="p-4">
                  <h3 className="font-display font-semibold text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-500">{p.developer} · {p.location}</p>
                  <p className="mt-2 text-sm"><span className="text-slate-500">{t("projects.from")}:</span> <span className="font-bold text-kp-maroon">{p.price_from}</span></p>
                  <p className="text-xs text-slate-500">{p.configuration}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("projects.possession")}: <b className="text-slate-700">{p.possession}</b></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Localities */}
      <section className="max-w-[1400px] mx-auto px-4 py-6 sm:py-16">
        <div className="text-center mb-4 sm:mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-kp-teal">Surat Real Estate</div>
          <h2 className="font-display text-2xl sm:text-4xl font-bold text-slate-900 mt-2">Popular Localities in Surat</h2>
          <p className="text-slate-600 mt-1 sm:mt-2 text-sm sm:text-base">Explore verified properties in Surat's most-searched neighbourhoods.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
          {["Vesu","Adajan","Piplod","Athwa","City Light","Althan","Pal","Dumas","Varachha","Katargam","Udhna","Pandesara","Sachin","Kadodara","Hazira"].map((l) => (
            <Link key={l} to={`/locality/${l.toLowerCase().replace(/\s+/g, "-")}`} data-testid={`popular-locality-${l.toLowerCase()}`}
              className="text-sm bg-white border border-slate-200 text-slate-700 hover:border-kp-teal hover:text-kp-teal hover:bg-kp-teal-light transition-colors px-4 py-2 rounded-full font-medium">
              {l}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 py-4 sm:py-14">
        <div className="rounded-2xl sm:rounded-3xl p-5 sm:p-10 text-white grid md:grid-cols-2 gap-4 sm:gap-6 items-center overflow-hidden relative"
          style={{ background: "linear-gradient(90deg, var(--kp-teal-dark) 0%, var(--kp-teal) 50%, var(--kp-teal-dark) 100%)" }}>
          <div>
            <h3 className="font-display text-xl sm:text-4xl font-bold leading-tight text-white">Ready to list your property?</h3>
            <p className="mt-1.5 sm:mt-2 text-xs sm:text-base text-white/85">Reach 1000s of genuine buyers & tenants in Surat. Free to list, verified by our team.</p>
          </div>
          <div className="flex flex-col sm:flex-row md:justify-end gap-2 sm:gap-3">
            <Link to="/post-property" className="w-full sm:w-auto">
              <Button className="btn-maroon rounded-full h-11 sm:h-12 w-full sm:w-auto px-6 sm:px-8 font-semibold" data-testid="cta-post">Post Property Free</Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button className="rounded-full h-11 sm:h-12 w-full sm:w-auto px-6 sm:px-8 font-semibold bg-white text-kp-teal-dark hover:bg-kp-teal-light hover:text-kp-teal-dark border-0" data-testid="cta-enquiry">Send Enquiry</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
